import type { ReactElement } from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded';
import SelectAllRoundedIcon from '@mui/icons-material/SelectAllRounded';
import { Chip, Tooltip } from '@mui/material';
import type { Highlight, HighlightLevel } from '../api/types';

/**
 * How exact the highlight on screen is, stated rather than implied.
 *
 * Approximate geometry that looks exact is the failure this exists to prevent: a
 * reader who trusts a box that is merely "the right line" will read the wrong
 * word off the scan and never know. Each state has its own icon and its own
 * wording, so the distinction survives a greyscale screen and a screen reader.
 */

const states: Record<HighlightLevel, { label: string; hint: string; icon: ReactElement; color: 'success' | 'warning' | 'default' }> = {
  word: {
    label: 'Wortgenau',
    hint: 'Die Markierung umschließt das gefundene Wort.',
    icon: <CheckCircleRoundedIcon fontSize="small" />,
    color: 'success'
  },
  line: {
    label: 'Zeilengenau',
    hint: 'Die Zeile stimmt; die Wortgrenzen sind auf dieser Seite nicht bekannt.',
    icon: <HorizontalRuleRoundedIcon fontSize="small" />,
    color: 'warning'
  },
  block: {
    label: 'Nur Bereich',
    hint: 'Nur der Textbereich ist bekannt, keine Zeile und kein Wort.',
    icon: <SelectAllRoundedIcon fontSize="small" />,
    color: 'warning'
  },
  unavailable: {
    label: 'Keine Koordinaten',
    hint: 'Für diese Seite liegen noch keine Positionsdaten vor. Der Text ist trotzdem durchsuchbar.',
    icon: <HelpOutlineRoundedIcon fontSize="small" />,
    color: 'default'
  }
};

export function PrecisionBadge({ status, hits, active }: {
  status?: HighlightLevel;
  hits?: Highlight[];
  active?: number;
}) {
  if (!status) return null;

  // The badge follows the hit being looked at, not the page average: a page can
  // hold one wordexact hit and one line-level one, and the reader needs to know
  // which kind is on screen right now.
  const current = hits && active !== undefined ? hits[active] : undefined;
  const level = current?.level ?? status;
  const state = states[level] ?? states.unavailable;

  const hint = current?.approximate && current.reason === 'substring'
    ? 'Der Suchbegriff steht innerhalb eines längeren Wortes; markiert ist das ganze Wort.'
    : state.hint;

  return (
    <Tooltip title={hint}>
      <Chip size="small" icon={state.icon} label={state.label} color={state.color} variant="outlined" />
    </Tooltip>
  );
}
